// Queries de NAVE.
//
// En c6 la nave se crea inline (paso 0 del mini-wizard de "Crear
// camada"). El listado de naves se carga en la Home y en
// AsignarLotesPage para saber qué naves tiene la explotación.

import { apiFetch, qk, useApiMutation, useApiQuery } from './client';
import type { Nave, NaveCreateInput, NaveUpdateInput, SensorEquipment } from '@/lib/types';

const NAVES_KEY = qk('naves');

/** Lista las naves vivas (no soft-deleted) de la explotación. */
export function useNaves() {
  return useApiQuery<Nave[]>({
    queryKey: [...NAVES_KEY],
    // El backend aún no expone un endpoint /v1/naves para listar todas
    // las de la explotación. Lo añadimos en este commit como endpoint
    // nuevo (GET /api/v1/naves?explotacion_id implícito por JWT). Si
    // el backend ya lo expone, ajustamos la URL.
    queryFn: () => apiFetch<Nave[]>('/v1/naves'),
    staleTime: 30 * 1000,
  });
}

export function useCrearNave() {
  return useApiMutation<NaveCreateInput, Nave>({
    mutationFn: (body, context) =>
      apiFetch<Nave>('/v1/naves', {
        offlineBase: context.offlineBase,
        method: 'POST',
        body: JSON.stringify(body),
      }),
    invalidateKeys: [NAVES_KEY],
  });
}

export function useActualizarNave() {
  return useApiMutation<{ id: number; body: NaveUpdateInput }, Nave>({
    mutationFn: ({ id, body }, context) =>
      apiFetch<Nave>(`/v1/naves/${id}`, {
        offlineBase: context.offlineBase,
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    invalidateKeys: [NAVES_KEY],
  });
}

export function useBorrarNave() {
  return useApiMutation<number, null>({
    mutationFn: (id, context) =>
      apiFetch<null>(`/v1/naves/${id}`, {
        offlineBase: context.offlineBase,
        method: 'DELETE',
      }),
    invalidateKeys: [NAVES_KEY],
  });
}

export function useGuardarSensoresNave() {
  return useApiMutation<
    {
      id: number;
      equipment: SensorEquipment;
      confirm_deactivation: boolean;
    },
    Nave
  >({
    mutationFn: ({ id, equipment, confirm_deactivation }, context) =>
      apiFetch<Nave>(`/v1/naves/${id}/sensores`, {
        offlineBase: context.offlineBase,
        method: 'PUT',
        body: JSON.stringify({ equipment, confirm_deactivation }),
      }),
    invalidateKeys: [NAVES_KEY, qk('iot', 'naves'), qk('iot', 'temperatura')],
  });
}
